<template>
  <div class="faq-section">
    <!-- Category Filter (if multiple categories) -->
    <div v-if="categories && categories.length > 1" class="flex flex-wrap justify-center gap-3 mb-12">
      <button
        @click="selectedCategory = null"
        class="px-4 py-2 rounded-lg text-sm font-medium transition-all"
        :class="selectedCategory === null
          ? 'bg-brand-primary text-white'
          : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'"
      >
        All
      </button>
      <button
        v-for="category in categories"
        :key="category"
        @click="selectedCategory = category"
        class="px-4 py-2 rounded-lg text-sm font-medium transition-all"
        :class="selectedCategory === category
          ? 'bg-brand-primary text-white'
          : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'"
      >
        {{ category }}
      </button>
    </div>

    <!-- FAQ Accordion -->
    <div class="space-y-4" itemscope itemtype="https://schema.org/FAQPage">
      <div
        v-for="(faq, index) in filteredFAQs"
        :key="index"
        class="border border-gray-700 rounded-xl overflow-hidden bg-gray-800 bg-opacity-40 hover:border-brand-primary transition-all"
        itemscope itemprop="mainEntity" itemtype="https://schema.org/Question"
      >
        <!-- Question Button -->
        <button
          @click="toggleFAQ(index)"
          class="w-full text-left px-6 py-5 flex items-center justify-between group"
          :aria-expanded="activeIndex === index"
        >
          <span class="text-lg font-semibold text-white pr-8 group-hover:text-brand-accent transition-colors" itemprop="name">
            {{ faq.question }}
          </span>
          <svg
            class="w-6 h-6 text-brand-primary flex-shrink-0 transition-transform duration-300"
            :class="{ 'rotate-180': activeIndex === index }"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
          </svg>
        </button>

        <!-- Answer Panel -->
        <div
          v-show="activeIndex === index"
          class="px-6 pb-5"
          itemscope itemprop="acceptedAnswer" itemtype="https://schema.org/Answer"
        >
          <div class="prose prose-invert max-w-none">
            <p class="text-gray-400 leading-relaxed" itemprop="text">
              {{ faq.answer }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Still have questions CTA -->
    <div class="mt-12 text-center p-8 bg-gray-800 bg-opacity-40 rounded-xl border border-gray-700">
      <h3 class="text-2xl font-bold text-white mb-3">
        Still have questions?
      </h3>
      <p class="text-gray-400 mb-6">
        Our team is here to help. Get in touch and we'll answer any questions you have.
      </p>
      <slot name="cta">
        <button
          @click="showSupportModal = true"
          class="inline-flex items-center px-6 py-3 bg-brand-primary text-white font-semibold rounded-lg hover:bg-brand-secondary transition-colors"
        >
          Contact Us
          <svg class="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
          </svg>
        </button>
      </slot>
    </div>

    <!-- Support Form Modal -->
    <SupportFormModal
      :show="showSupportModal"
      @close="showSupportModal = false"
      @submitted="showSupportModal = false"
    />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import SupportFormModal from '@/Components/SupportFormModal.vue';

const props = defineProps({
  faqs: {
    type: Array,
    required: true,
    validator: (faqs) => {
      return faqs.every(faq => faq.question && faq.answer);
    }
  },
  categories: {
    type: Array,
    default: () => []
  },
  defaultOpen: {
    type: Number,
    default: null
  }
});

// State
const activeIndex = ref(props.defaultOpen);
const selectedCategory = ref(null);
const showSupportModal = ref(false);

// Computed
const filteredFAQs = computed(() => {
  if (!selectedCategory.value) {
    return props.faqs;
  }
  return props.faqs.filter(faq => faq.category === selectedCategory.value);
});

// Methods
const toggleFAQ = (index) => {
  activeIndex.value = activeIndex.value === index ? null : index;
};

const filterByCategory = (category) => {
  selectedCategory.value = category;
  activeIndex.value = null; // Close any open FAQ when changing category
};
</script>
